import { useEffect, useMemo, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button, Input, Modal, Select, toast } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useAuthStore } from '../auth/store';
import {
  useCancelScheduledSend,
  useDeliveryStatus,
  useScheduleSend,
  type InvoiceDetail,
} from './api';
import { EnvironmentStrip, deliveryNote, paymentNote } from './composer';
import { useCopyMe } from './recipients';

/**
 * Schedule a DRAFT to send itself (Jesse, 2026-10-14: *"can we have it so I can
 * schedule a send?"*). The server's rules are in
 * `backend/src/modules/invoices/scheduled-send.ts`; this screen only asks for a
 * day and an hour and says, before anything is saved, what will happen then.
 *
 * The message is composed WHEN IT GOES — the server-drafted invoice email to
 * the client's billing email + CC as they stand that day, the invoice issued
 * that day. So there is no message to edit here; to write your own, send it
 * now from the composer.
 */

/** Hours offered, in the org's own zone — mirrors `SCHEDULED_SEND_HOURS`. */
const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const DEFAULT_HOUR = 9;
const FALLBACK_ZONE = 'America/Chicago';

function hourLabel(hour: number): string {
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function partsIn(at: Date, timeZone: string): { day: string; hour: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { day: `${get('year')}-${get('month')}-${get('day')}`, hour: Number(get('hour')) % 24 };
}

function addDays(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/** "Tue, Oct 20 at 9 AM CDT" — the instant, read in the org's zone. */
export function formatScheduled(iso: string, timeZone: string | undefined): string {
  const zone = timeZone || FALLBACK_ZONE;
  const at = new Date(iso);
  const day = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' }).format(at);
  const time = new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: 'numeric', timeZoneName: 'short' }).format(at);
  return `${day} at ${time}`;
}

/** "Oct 20" — short form for a list badge. */
export function formatScheduledShort(iso: string, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || FALLBACK_ZONE,
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
}

export function ScheduleSendDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const status = useDeliveryStatus();
  const zone = status.data?.timeZone || FALLBACK_ZONE;
  const myEmail = useAuthStore((s) => s.user?.email ?? null);
  const schedule = useScheduleSend();
  const [rememberedCopyMe, setRememberedCopyMe] = useCopyMe();

  const [day, setDay] = useState('');
  const [hour, setHour] = useState(DEFAULT_HOUR);
  const [copyMe, setCopyMe] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed on open only — never on a refetch, which would throw away what was typed.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (invoice.scheduledSend) {
      const p = partsIn(new Date(invoice.scheduledSend.at), zone);
      setDay(p.day);
      setHour(HOURS.includes(p.hour) ? p.hour : DEFAULT_HOUR);
      setCopyMe(Boolean(invoice.scheduledSend.copyTo));
    } else {
      const today = partsIn(new Date(), zone).day;
      // A draft dated ahead was written for that day; otherwise tomorrow.
      setDay(invoice.issueDate > today ? invoice.issueDate : addDays(today, 1));
      setHour(DEFAULT_HOUR);
      setCopyMe(rememberedCopyMe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const to = invoice.client.billingEmail ?? null;
  const cc = invoice.client.billingEmailCc ?? [];
  const nobody = !to && cc.length === 0;

  // The terms the invoice will carry once it goes out on that day.
  const termsDays = Math.max(
    0,
    Math.round((Date.parse(`${invoice.dueDate}T00:00:00Z`) - Date.parse(`${invoice.issueDate}T00:00:00Z`)) / 86_400_000),
  );
  const dueOn = useMemo(() => (/^\d{4}-\d{2}-\d{2}$/.test(day) ? addDays(day, termsDays) : null), [day, termsDays]);
  const zoneName = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'long' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? zone,
    [zone],
  );

  const submit = async () => {
    setError(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      setError('Pick the day to send it.');
      return;
    }
    try {
      const res = await schedule.mutateAsync({ id: invoice.id, on: day, hour, copyMe });
      setRememberedCopyMe(copyMe);
      toast.success(`#${invoice.number} will send ${formatScheduled(res.scheduledSendAt, res.timeZone)}.`);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The schedule could not be saved.');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={invoice.scheduledSend ? `Change when #${invoice.number} sends` : `Schedule #${invoice.number} to send`}
      description="It goes out by itself at that time, exactly as Send invoice would send it now."
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          {error && (
            <p role="alert" className="mr-auto text-body-sm text-status-late">
              {error}
            </p>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} loading={schedule.isPending} disabled={nobody}>
            <CalendarClock className="h-4 w-4" aria-hidden />
            {invoice.scheduledSend ? 'Save schedule' : 'Schedule send'}
          </Button>
        </div>
      }
    >
      <EnvironmentStrip facts={[deliveryNote(status.data), paymentNote(status.data)]} className="mb-3" />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_9rem]">
          <Input label="Send on" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <Select
            label="At"
            options={HOURS.map((h) => ({ value: String(h), label: hourLabel(h) }))}
            value={String(hour)}
            onChange={(e) => setHour(Number(e.target.value))}
          />
        </div>
        <p className="text-caption text-grey-600">{zoneName}</p>

        <dl className="space-y-1.5 rounded-lg border border-grey-200 px-3 py-2.5 text-body-sm">
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-grey-500">To</dt>
            <dd className="num min-w-0 break-words text-secondary">
              {nobody ? (
                <span className="text-status-late">
                  {invoice.client.name} has no billing email — add one on the client first.
                </span>
              ) : (
                [to, ...cc.map((c) => `cc ${c}`)].filter(Boolean).join(', ')
              )}
            </dd>
          </div>
          {dueOn && (
            <div className="flex gap-2">
              <dt className="w-16 shrink-0 text-grey-500">Issued</dt>
              <dd className="num text-secondary">
                {formatDate(day)} · due {formatDate(dueOn)}
              </dd>
            </div>
          )}
        </dl>
        <p className="text-caption text-grey-600">
          The standard invoice email goes to whoever is on the client that day. To write your own message, send it
          now instead.
        </p>

        {myEmail && (
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-grey-200 px-3 py-2.5">
            <input
              type="checkbox"
              checked={copyMe}
              onChange={(e) => setCopyMe(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
            />
            <span className="min-w-0">
              <span className="block text-body-sm font-semibold text-secondary">Send a copy to me</span>
              <span className="num block text-caption text-grey-600">{myEmail} · blind-copied, so the client does not see it</span>
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}

/**
 * The line under a draft's header: when it sends and how to stop it — or, when
 * the last schedule could not send, why. Renders nothing otherwise.
 */
export function ScheduledSendNotice({
  invoice,
  onChange,
}: {
  invoice: InvoiceDetail;
  onChange: () => void;
}) {
  const status = useDeliveryStatus();
  const cancel = useCancelScheduledSend();
  if (invoice.state !== 'DRAFT') return null;

  if (invoice.scheduledSend) {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5 text-body-sm">
        <CalendarClock className="h-4 w-4 shrink-0 text-accent" aria-hidden />
        <span className="min-w-[13rem] flex-1 text-secondary">
          <span className="block font-semibold">Scheduled to send {formatScheduled(invoice.scheduledSend.at, status.data?.timeZone)}</span>
          {invoice.scheduledSend.by && <span className="block text-caption text-grey-600">Set by {invoice.scheduledSend.by}</span>}
        </span>
        <span className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onChange}>
            Change
          </Button>
          <Button
            size="sm"
            variant="outline"
            loading={cancel.isPending}
            onClick={() =>
              cancel.mutate(invoice.id, {
                onSuccess: () => toast.success(`#${invoice.number} is no longer scheduled. It stays a draft.`),
                onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not cancel the schedule.'),
              })
            }
          >
            Don’t send
          </Button>
        </span>
      </div>
    );
  }

  const last = invoice.lastScheduledSend;
  if (last && (last.outcome === 'refused' || last.outcome === 'failed' || last.outcome === 'missed')) {
    return (
      <div
        role="alert"
        className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-status-partial-border bg-status-partial-bg px-3 py-2.5 text-body-sm"
      >
        <CalendarClock className="h-4 w-4 shrink-0 text-status-partial" aria-hidden />
        <span className="min-w-[13rem] flex-1 text-secondary">
          <span className="font-semibold">
            The send scheduled for {formatScheduled(last.scheduledFor, status.data?.timeZone)} did not go.
          </span>{' '}
          <span className="text-grey-600">{last.message}</span>
        </span>
        <Button size="sm" variant="outline" onClick={onChange}>
          Schedule again
        </Button>
      </div>
    );
  }
  return null;
}
